import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Sidebar } from './components/layout/Sidebar';
import { MobileShell } from './components/layout/MobileShell';
import { MobileMenu } from './pages/MobileMenu';
import { Welcome } from './components/common/Welcome';
import { VehiclePhone } from './pages/VehiclePhone';
import { isNative } from './utils/native';
import { Login } from './pages/Login';
import { Team } from './pages/Team';
import { Join } from './pages/Join';
import { Dashboard } from './pages/Dashboard';
import { Vehicles } from './pages/Vehicles';
import { VehicleNew } from './pages/VehicleNew';
import { VehicleDetail } from './pages/VehicleDetail';
import { Insurance } from './pages/Insurance';
import { RoadTax } from './pages/RoadTax';
import { Mechanical } from './pages/Mechanical';
import { Services } from './pages/Services';
import { Documents } from './pages/Documents';
import { Expenses } from './pages/Expenses';
import { Forms } from './pages/Forms';
import { Alerts } from './pages/Alerts';
import { Reports } from './pages/Reports';
import { Settings } from './pages/Settings';
import { Billing } from './pages/Billing';
import { ThemeProvider } from './hooks/useTheme';
import { ClassicCarIntelligence } from './pages/ClassicCarIntelligence';
import { useOwner } from './hooks/useOwner';
import { Roadmap } from './pages/Roadmap';
import { Portfolio } from './pages/Portfolio';
import { VehiclePassport } from './pages/VehiclePassport';
import { ResetPassword } from './pages/ResetPassword';
import { BulkImport } from './pages/BulkImport';
import './index.css';
import './mobile.css';

import { BrandLogo } from './components/common/BrandLogo';
const ProtectedLayout = ({ children }) => {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  if (loading) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div style={{ textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto 16px' }} />
        <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading Car Collector Studio…</div>
      </div>
    </div>
  );

  if (!user) return <Navigate to="/login" replace />;

  // Inside the iPhone / Android app: phone layout (bottom tabs) instead of the web sidebar.
  if (isNative) return <MobileShell>{children}</MobileShell>;

  return (
    <div className="app-layout" style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

      {/* Mobile topbar */}
      <div className="mobile-topbar">
        <div className="mobile-topbar-logo" onClick={() => navigate('/portfolio')} style={{ cursor: 'pointer' }}>
          <BrandLogo variant="white" width={150} />
        </div>
        <button className="hamburger" onClick={() => setSidebarOpen(o => !o)}>
          <span style={{ transform: sidebarOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }}/>
          <span style={{ opacity: sidebarOpen ? 0 : 1 }}/>
          <span style={{ transform: sidebarOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }}/>
        </button>
      </div>

      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 149, backdropFilter: 'blur(4px)' }}
        />
      )}

      {/* Sidebar */}
      <div style={{
        position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 150,
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s ease',
      }}
      className="mobile-sidebar">
        <Sidebar onNavigate={() => setSidebarOpen(false)} />
      </div>

      {/* Desktop sidebar — always visible */}
      <div className="desktop-sidebar">
        <Sidebar onNavigate={() => setSidebarOpen(false)} />
      </div>

      {/* Main content */}
      <div className="main-content" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {children}
      </div>

    </div>
  );
};

const RoleProtectedRoute = ({ children, allowedRoles }) => {
  const { role, loading } = useOwner();
  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!allowedRoles.includes(role)) return <Navigate to="/" replace />;
  return children;
};

// How long the phone app can sit in the background before the slide-to-enter screen shows again.
const WELCOME_AGAIN_AFTER_MS = 5 * 60 * 1000;

const AppRoutes = () => {
  const { user, loading } = useAuth();

  // Phone app: the slide-to-enter screen shows when the app opens, and again after the app has
  // been in the background for a few minutes. It sits on top of the screens, so you come back
  // to exactly where you were.
  const [welcome, setWelcome] = useState(isNative);
  useEffect(() => {
    if (!isNative) return undefined;
    let hiddenAt = 0;
    const onVisibility = () => {
      if (document.hidden) { hiddenAt = Date.now(); return; }
      if (hiddenAt && Date.now() - hiddenAt > WELCOME_AGAIN_AFTER_MS) setWelcome(true);
      hiddenAt = 0;
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
  const welcomeScreen = isNative && welcome
    ? <Welcome signedIn={!!user} loading={loading} onEnter={() => setWelcome(false)} />
    : null;

  // Same position in the tree as below, so the welcome screen is not rebuilt when loading ends.
  if (loading) return <>{null}{welcomeScreen}</>;

  return (
    <>
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Web: Portfolio is the post-sign-in landing page. Phone app: the Collection is. */}
      <Route path="/" element={isNative ? <Navigate to="/vehicles" replace /> : <ProtectedLayout><Portfolio /></ProtectedLayout>} />
      <Route path="/portfolio" element={<ProtectedLayout><Portfolio /></ProtectedLayout>} />
      <Route path="/dashboard" element={<ProtectedLayout><Dashboard /></ProtectedLayout>} />
      <Route path="/vehicles/:id/passport" element={<VehiclePassport />} />
      <Route path="/vehicles" element={<ProtectedLayout><Vehicles /></ProtectedLayout>} />
      <Route path="/vehicles/new" element={<ProtectedLayout><RoleProtectedRoute allowedRoles={['owner','admin','editor']}><VehicleNew /></RoleProtectedRoute></ProtectedLayout>} />
      <Route path="/vehicles/:id" element={<ProtectedLayout>{isNative ? <VehiclePhone /> : <VehicleDetail />}</ProtectedLayout>} />
      <Route path="/vehicles/:id/records" element={<ProtectedLayout><VehicleDetail /></ProtectedLayout>} />
      <Route path="/vehicles/:id/edit" element={<ProtectedLayout><VehicleDetail /></ProtectedLayout>} />
      <Route path="/insurance" element={<ProtectedLayout><Insurance /></ProtectedLayout>} />
      <Route path="/road-tax" element={<ProtectedLayout><RoadTax /></ProtectedLayout>} />
      <Route path="/mechanical" element={<ProtectedLayout><Mechanical /></ProtectedLayout>} />
      <Route path="/services" element={<ProtectedLayout><Services /></ProtectedLayout>} />
      <Route path="/documents" element={<ProtectedLayout><Documents /></ProtectedLayout>} />
      <Route path="/expenses" element={<ProtectedLayout><Expenses /></ProtectedLayout>} />
      <Route path="/forms" element={<ProtectedLayout><Forms /></ProtectedLayout>} />
      <Route path="/menu" element={<ProtectedLayout><MobileMenu /></ProtectedLayout>} />
      <Route path="/alerts" element={<ProtectedLayout><Alerts /></ProtectedLayout>} />
      <Route path="/reports" element={<ProtectedLayout><Reports /></ProtectedLayout>} />
      <Route path="/classic-car-intelligence" element={<ProtectedLayout><ClassicCarIntelligence /></ProtectedLayout>} />
      <Route path="/import" element={<ProtectedLayout><RoleProtectedRoute allowedRoles={['owner','admin']}><BulkImport /></RoleProtectedRoute></ProtectedLayout>} />
      <Route path="/roadmap" element={<ProtectedLayout><RoleProtectedRoute allowedRoles={['owner']}><Roadmap /></RoleProtectedRoute></ProtectedLayout>} />
      <Route path="*" element={<Navigate to="/" replace />} />
      <Route path="/join" element={<Join />} />
      <Route path="/team" element={
        <ProtectedLayout>
          <RoleProtectedRoute allowedRoles={['owner','admin']}>
            <Team />
          </RoleProtectedRoute>
        </ProtectedLayout>
      } />
      <Route path="/settings" element={
        <ProtectedLayout>
          <RoleProtectedRoute allowedRoles={['owner']}>
            <Settings />
          </RoleProtectedRoute>
        </ProtectedLayout>
      } />
      <Route path="/billing" element={
        <ProtectedLayout>
          <RoleProtectedRoute allowedRoles={['owner']}>
            <Billing />
          </RoleProtectedRoute>
        </ProtectedLayout>
      } />
    </Routes>
    {welcomeScreen}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster
          position="top-right"
          containerStyle={{ top: 'calc(16px + var(--sat, 0px))', right: 16 }}
          toastOptions={{
            style: {
              background: 'var(--surface)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              fontSize: '13.5px',
            },
            success: { iconTheme: { primary: 'var(--green)', secondary: 'white' } },
            error: { iconTheme: { primary: 'var(--red)', secondary: 'white' } },
          }}
        />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
